/**
 * Raw Products Filters Component
 *
 * Filters bar for the raw products list.
 */

import { Search, X } from 'lucide-react';
import { Input, Select, ChipGroup } from '@/components/ui';
import type { ChipOption } from '@/components/ui';
import { useFlatCategories } from '../hooks/useCategories';
import { MarketType, MarketTypeLabels } from '@bstock/shared';

// Market type chip options with colors
const MARKET_CHIP_OPTIONS: ChipOption<MarketType>[] = [
  { value: MarketType.FOOD, label: MarketTypeLabels[MarketType.FOOD], color: 'rgb(var(--color-success-500))' },
  { value: MarketType.FOOD_IND, label: MarketTypeLabels[MarketType.FOOD_IND], color: 'rgb(var(--color-success-400))' },
  { value: MarketType.NON_FOOD, label: MarketTypeLabels[MarketType.NON_FOOD], color: 'rgb(var(--color-primary-500))' },
  { value: MarketType.NON_FOOD_IND, label: MarketTypeLabels[MarketType.NON_FOOD_IND], color: 'rgb(var(--color-primary-400))' },
  { value: MarketType.MAINTENANCE, label: MarketTypeLabels[MarketType.MAINTENANCE], color: 'rgb(var(--color-warning-500))' },
];

interface RawProductsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  categoryId: string;
  onCategoryChange: (value: string | undefined) => void;
  marketTypes: MarketType[];
  onMarketTypesChange: (value: MarketType[]) => void;
  isActive: boolean | undefined;
  onIsActiveChange: (value: boolean | undefined) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const statusOptions = [
  { value: 'true', label: 'Actif' },
  { value: 'false', label: 'Inactif' },
];

export function RawProductsFilters({
  search,
  onSearchChange,
  categoryId,
  onCategoryChange,
  marketTypes,
  onMarketTypesChange,
  isActive,
  onIsActiveChange,
  onReset,
  hasActiveFilters,
}: RawProductsFiltersProps) {
  const { data: categories, isLoading: categoriesLoading } = useFlatCategories();

  // Build tree options for category select
  const categoryTreeOptions = categories?.map((cat) => ({
    value: cat.id,
    label: cat.name,
    depth: cat.depth,
  })) ?? [];

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm">
      {/* Tous les filtres sur une seule ligne - flex-wrap pour responsive */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Recherche */}
        <div className="relative w-[300px] flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher un produit (nom, SKU, code-barres)..."
            className="pl-9"
          />
        </div>

        <div className="w-[200px] flex-shrink-0">
          <Select
            value={categoryId || ''}
            onChange={(value) => onCategoryChange(value || undefined)}
            treeOptions={categoryTreeOptions}
            placeholder="Toutes les categories"
            disabled={categoriesLoading}
          />
        </div>

        <div className="w-[160px] flex-shrink-0">
          <Select
            value={isActive === undefined ? 'true' : isActive.toString()}
            onChange={(value) => {
              onIsActiveChange(value === 'true');
            }}
            options={statusOptions}
          />
        </div>

        <ChipGroup
          options={MARKET_CHIP_OPTIONS}
          value={marketTypes}
          onChange={onMarketTypesChange}
          label="Marche:"
          emptyMeansAll
        />

        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors ml-auto"
          >
            <X className="w-4 h-4" />
            Reinitialiser
          </button>
        )}
      </div>
    </div>
  );
}
